import React, { useEffect, useRef, useState } from "react";
import * as d3 from "d3";
import {
  calculateGrowth,
  COLOR_PALETTE_LIST,
  darkblueColor,
  darkgreenColor,
  lightBrownColor,
  lightgreencolor,
  midgrayColor,
  midGreenColor,
} from "components/Reports/data";
import MarimekkoSkeleton from "./MarimekkoSkeleton";
import { formatFixedOneNotation, formatIndianNumber } from "components/Reports/DashboardComponents/Home/StoreListCards/data";

export default function MarimekkoChart({
  data,
  width = 1560,
  height = 700,
  margin = { top: 40, right: 10, bottom: 60, left: 40 },
  colors,
  loading,
  selectedSegemnt,
  setSelectedSegemnt
}) {
  const ref = useRef(null);
  const tooltipRef = useRef(null);


  useEffect(() => {
    if (!data || data.length === 0) return;

    const svg = d3.select(ref.current);
    svg.selectAll("*").remove();

    const innerWidth = width - margin.left - margin.right;
    const innerHeight = height - margin.top - margin.bottom;
    const minWidthToShowText = 25;

    const g = svg
      .attr("width", width)
      .attr("height", height)
      .append("g")
      .attr("transform", `translate(${margin.left},${margin.top})`);

    // Tooltip
    let tooltip;
    if (!tooltipRef.current) {
      tooltip = d3
        .select("body")
        .append("div")
        .style("position", "absolute")
        .style("background", "var(--color-tooltip-background)")
        .style("color", "var(--color-tooltip)")
        .style("padding", "6px 10px")
        .style("border-radius", "4px")
        .style("pointer-events", "none")
        .style("opacity", 0)
        .style("font-size", "var(--font-size-tooltip)");
      tooltipRef.current = tooltip;
    } else {
      tooltip = d3.select(tooltipRef.current.node());
    }

    // Utility: truncate text with ellipsis
    function truncateText(selection, maxWidth) {
      selection.each(function () {
        const text = d3.select(this);
        const fullText = text.text();
        let truncated = fullText;
        while (this.getComputedTextLength() > maxWidth && truncated.length > 0) {
          truncated = truncated.slice(0, -1);
          text.text(truncated + "…");
        }
        text.attr("data-fulltext", fullText);
      });
    }

    // Tooltip helpers
    const showTooltip = (event, content) => {
      tooltip.transition().duration(100).style("opacity", 1);
      tooltip
        .html(content)
        .style("left", event.pageX + 10 + "px")
        .style("top", event.pageY - 28 + "px");
    };
    const hideTooltip = () => tooltip.transition().duration(100).style("opacity", 0);

    // Compute totals
    const categories = data.map((d) => ({
      category: d.category,
      segments: d.segments,
      total: d.segments.reduce((sum, s) => sum + s.sales, 0),
    }));

    const grandTotal = d3.sum(categories, (d) => d.total);
    const xScale = d3.scaleLinear().domain([0, grandTotal]).range([0, innerWidth]);
    const yScale = d3.scaleLinear().domain([0, 1]).range([innerHeight, 0]);

    // Compute normalized column widths
    let currentX = 0;
    const rawWidths = categories.map((d) => xScale(d.total));
    const totalWidth = d3.sum(rawWidths);
    const correction = innerWidth / (totalWidth || 1);

    const columns = categories.map((d, i) => {
      const w = Math.max(0, rawWidths[i] * correction);
      const x0 = currentX;
      currentX += w;
      return { ...d, x0, width: w, color: colors[i % colors.length] };
    });

    columns.forEach((col) => {
      const colGroup = g.append("g").attr("transform", `translate(${col.x0},0)`);
      let y0 = 0;

      const safeTotal = col.total > 0 ? col.total : 1;

      col.segments.forEach((seg) => {
        const realSales = seg.sales || 0;
        const ly_sales = seg.ly_sales || 0;

        const safeSales = Math.max(0, realSales);
        
        const ratio = isFinite(safeSales / safeTotal) ? safeSales / safeTotal : 0;
        const segHeight = Math.max(0, ratio * innerHeight);
        const rectY = Math.max(0, innerHeight - y0 - segHeight);

        const contribution = (ratio * 100).toFixed(1);
        const formattedSales = formatFixedOneNotation(safeSales, "value");
        const formattedLy_Sales = formatFixedOneNotation(ly_sales, "value");
        const growth = calculateGrowth(ly_sales, safeSales)

        const growthValue = parseFloat(growth).toFixed(1); // ensure it's a number
        const growthColor = growthValue >= 0 ? "green" : "red";

        const isSelected =
          selectedSegemnt &&
          selectedSegemnt.segment.type === seg.type &&
          selectedSegemnt.category === col.category;

        // ✅ Avoid NaN attributes completely
        if (!isFinite(rectY) || !isFinite(segHeight)) return;

        colGroup
          .append("rect")
          .attr("x", 0)
          .attr("y", rectY)
          .attr("width", col.width)
          .attr("height", segHeight)
          .attr("fill", col.color)
          .attr("stroke", isSelected ? "#000" : "#fff")
          .attr("stroke-width", isSelected ? 2 : 1)
          .style("cursor", "pointer")
          .style("opacity", selectedSegemnt && !isSelected ? 0.4 : 1)
          .on("mouseover", (e) =>
            showTooltip(
              e,
              `<span>${seg.type} ${col.category} <b>(${contribution}%)</b></span><br/>TY: ${formattedSales} <span style="color:${growthColor}; font-weight:bold;">(${growthValue}%)</span><br/>LY : ${formattedLy_Sales}`
            )
          )
          .on("mousemove", (e) =>
            showTooltip(
              e,
              `<span>${seg.type} ${col.category} <b>(${contribution}%)</b></span><br/>TY: ${formattedSales} <span style="color:${growthColor}; font-weight:bold;">(${growthValue}%)</span><br/>LY : ${formattedLy_Sales}`
            )
          )
          .on("mouseout", hideTooltip)
          .on("click", () => {
            const newSelection =
              selectedSegemnt &&
              selectedSegemnt.segment.type === seg.type &&
              selectedSegemnt.category === col.category
                ? null
                : { category: col.category, segment: seg };
            setSelectedSegemnt(newSelection);
          });


        // Label inside segment
        if (segHeight > 15 && col.width > minWidthToShowText) {
          const textEl = colGroup
            .append("text")
            .attr("x", col.width / 2)
            .attr("y", rectY + segHeight / 2)
            .attr("text-anchor", "middle")
            .attr("dominant-baseline", "middle")
            .style("fill", "white")
            .style("font-size", "var(--font-size-body1)")
            .style("cursor", "pointer")
            .text(`${seg.type} (${contribution}%)`);

          textEl.call(truncateText, col.width - 6);
          textEl
            .on("mouseover", (e) =>
              showTooltip(
                e,
                `<span>${seg.type} ${col.category} <b>(${contribution}%)</b></span><br/>TY : ${formattedSales} <span style="color:${growthColor}; font-weight:bold;">(${growthValue}%)</span><br/>LY: ${formattedLy_Sales}`
              )
            )
            .on("mousemove", (e) =>
              showTooltip(
                e,
                `<span>${seg.type} ${col.category} <b>(${contribution}%)</b></span><br/>TY : ${formattedSales} <span style="color:${growthColor}; font-weight:bold;">(${growthValue}%)</span><br/>LY: ${formattedLy_Sales}`
              )
            )
            .on("mouseout", hideTooltip)
            .on("click", () => {
            const newSelection =
              selectedSegemnt &&
              selectedSegemnt.segment.type === seg.type &&
              selectedSegemnt.category === col.category
                ? null
                : { category: col.category, segment: seg };
            setSelectedSegemnt(newSelection);
          });

        }

        y0 += segHeight;
      });

      // Category label
      if (col.width > minWidthToShowText) {
        const catText = g
          .append("text")
          .attr("x", col.x0 + col.width / 2)
          .attr("y", innerHeight + 15)
          .attr("text-anchor", "middle")
          .style("font-size", "var(--font-size-body1)")
          .style("font-weight", "bold")
          .text(col.category);

        catText.call(truncateText, col.width - 4);
        catText
          .on("mouseover", (e) =>
            showTooltip(e, `<strong>${catText.attr("data-fulltext")}</strong>`)
          )
          .on("mousemove", (e) =>
            showTooltip(e, `<strong>${catText.attr("data-fulltext")}</strong>`)
          )
          .on("mouseout", hideTooltip);
      }

      // Total label (formatted)
      if (col.width > minWidthToShowText) {
        const formattedTotal = formatFixedOneNotation(col.total, "value");

        // Calculate column contribution
        const colContri = ((col.total / grandTotal) * 100).toFixed(1);
        const totalText = g
          .append("text")
          .attr("x", col.x0 + col.width / 2)
          .attr("y", -5)
          .attr("text-anchor", "middle")
          .style("font-size", "var(--font-size-body1)")
          .style("font-weight", "bold")
          .text(`${formattedTotal} (${colContri}%)`);

        totalText.call(truncateText, col.width - 4);
        totalText
          .on("mouseover", (e) =>
      showTooltip(
        e,
        `<strong>Total:</strong> ${formatFixedOneNotation(col.total, "value").toLocaleString("en-IN")}<br/>
         <strong>Contribution:</strong> ${colContri}%`
      )
    )
    .on("mousemove", (e) =>
      showTooltip(
        e,
        `<strong>Total:</strong> ${formatFixedOneNotation(col.total, "value").toLocaleString("en-IN")}<br/>
         <strong>Contribution:</strong> ${colContri}%`
      )
    )
    .on("mouseout", hideTooltip);
      }
    });

    // Y-axis
    const yAxis = d3
      .axisLeft(yScale)
      .tickValues([0.2, 0.4, 0.6, 0.8, 1])
      .tickFormat(d3.format(".0%"))
      .tickSize(0);

    g.append("g").call(yAxis).selectAll("text").style("font-size", "var(--font-size-body3)");

    // X-axis baseline
    g.append("line")
      .attr("x1", 0)
      .attr("y1", innerHeight)
      .attr("x2", innerWidth)
      .attr("y2", innerHeight)
      .attr("stroke", "black");

    return () => {
      if (tooltipRef.current) {
        tooltipRef.current.remove();
        tooltipRef.current = null;
      }
    };
  }, [data, width, height, margin, colors]);


  return loading ? (
    <MarimekkoSkeleton width={width} height={height - 30} />
  ) : (
    <svg ref={ref}></svg>
  );
}
